import React, {useEffect, useState} from "react";
import {
    ArrowLeft,
    Edit,
    Mail,
    MapPin,
    Phone,
    User,
} from "lucide-react";
import {useNavigate, useParams} from "react-router-dom";
import { useSelector } from "react-redux";

import {
    useUpdateEmployeeRegistrationMutation,
    useGetEmployeeProfileQuery,
} from "../../../src/features/userApiSlice";

import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import {toast} from "react-toastify";

export default function EmployeeProfile() {

    const navigate = useNavigate();

    const {id, userId} =useParams();
    const projectId = String(id);

    const {
        data: usersProfile,
        isLoading,
        isError,
        refetch,
    } = useGetEmployeeProfileQuery(projectId, {
        skip: !projectId,
    });

    const [updateEmployeeRegistration] = useUpdateEmployeeRegistrationMutation();
    const userExist = usersProfile;

    const [isEditing, setIsEditing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Editing User Profile
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [employeeNumber, setEmployeeNumber] = useState("");
    // const [password, setPassword] = useState("");
    // const [confirmPassword, setConfirmPassword] = useState("");

    const editOnSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

            if (!id) {
            toast.error("Invalid Project Id");
            return;

            if (!firstName.trim() || !lastName.trim() || !email.trim()) {
                toast.error("First name, last name, and email are required.");
                return;
            }
        } try {
            setIsUpdating(true);

            await updateEmployeeRegistration({
                id : projectId,
                email : email,
                firstName : firstName,
                lastName : lastName,
                address : address,
                city : city,
                state : state,
                zip : zip,
                employeeNumber : employeeNumber,
                phoneNumber : phoneNumber,
                // password: password,
            }).unwrap()
            toast.success("Successfully updated employee profile");

            setIsUpdating(false);

            await refetch()

            // Wait 5 seconds
            await new Promise((resolve) => setTimeout(resolve, 3000));

            // Navigate back
            navigate(-1)

        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsUpdating(false);
        }
    }

    useEffect(() => {
        if (userExist) {
            setFirstName(userExist?.firstName ?? "");
            setLastName(userExist?.lastName ?? "");
            setEmail(userExist?.email ?? "");
            setAddress(userExist?.address ?? "");
            setCity(userExist?.city ?? "");
            setState(userExist?.state ?? "");
            setZip(userExist?.zip ?? "");
            setPhoneNumber(userExist?.phoneNumber ?? "");
            setEmployeeNumber(userExist?.employeeNumber ?? "");
        }
    }, [userExist, userExist?.firstName, userExist?.lastName, userExist?.email, userExist?.city,
        userExist?.zip, userExist?.state, userExist?.address]);

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-800">
                <CustomLoaderSmall />
            </div>
        );
    }

    if (isError || !usersProfile) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-800 p-6">

                <div className="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-lg">

                    <h2 className="text-xl font-semibold text-gray-900">
                        Employee Profile Not Found
                    </h2>

                    <p className="mt-2 text-sm text-gray-500">
                        We could not load this employee profile.
                    </p>

                    <button
                        onClick={() => navigate("/users")}
                        className="mt-5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        Back to Employees
                    </button>

                </div>

            </div>
        );
    }

    // ========================================================
    // Initials
    // ========================================================
    const initials =
        `${usersProfile.firstName?.charAt(0) ?? ""}${usersProfile.lastName?.charAt(0) ?? ""}`
            .toUpperCase();
    // ========================================================
    // Render
    // ========================================================

    // @ts-ignore
    // @ts-ignore
    return (
        <div className="min-h-screen bg-gray-800 p-4 sm:p-6 lg:p-8">

            <div className="mx-auto max-w-5xl">

                <div className="mb-6 flex items-center justify-between">

                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-white transition hover:text-blue-300"
                    >

                        <ArrowLeft size={24} />

                        <span>
                            Employees
                        </span>

                    </button>

                    <button
                        onClick={() =>
                            setIsEditing(true)
                        }
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                    >
                        <Edit size={17} />
                        <span>
                            Edit Employee
                        </span>
                    </button>

                </div>

                { isEditing ? (
                    <form onSubmit={editOnSubmitHandler}
                            className={"mb-6 rounded-xl bg-white p-6 shadow-sm"}>

                        <h2 className={"mb-5 text-lg font-semibold text-gray-900"}>
                            Edit Personal Information
                        </h2>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <label className="text-sm text-gray-700">
                                First Name
                                <input
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                Last Name
                                <input
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                Email
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                Phone Number
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700 sm:col-span-2">
                                Address
                                <input
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                City
                                <input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                State
                                <input
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                ZIP Code
                                <input
                                    value={zip}
                                    onChange={(e) => setZip(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>

                            <label className="text-sm text-gray-700">
                                Employee Number
                                <input
                                    value={employeeNumber}
                                    onChange={(e) => setEmployeeNumber(e.target.value)}
                                    className="mt-1 w-full rounded-lg border p-3"
                                />
                            </label>
                        </div>

                        <div className={"mt-6 flex justify-end gap-3"}>
                            <button
                                type={"button"}
                                onClick={() => setIsEditing(false)}
                                disabled={isUpdating}
                                className={"rounded-lg border px-4 py-2 text-gray-800 hover:text-blue-700"}
                            >
                                Cancel
                            </button>
                            <button
                                type={"submit"}
                                disabled={isUpdating}
                                className={"rounded-lg bg-blue-500 px-4 py-2 text-white disabled:opacity-50 hover:bg-blue-600"}>
                                {isUpdating ? "Saving..." : "Saving Changes"}
                            </button>
                        </div>

                    </form>
                ) : (
                    <div>
                        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm">

                            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">

                                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-blue-100 text-2xl font-bold text-blue-700">
                                    {initials}
                                </div>

                                <div className="flex-1">

                                    <h1 className="text-2xl font-bold text-gray-900">

                                        {usersProfile.firstName}{" "}
                                        {usersProfile.lastName}

                                    </h1>

                                    <p className="mt-1 text-sm text-gray-500">
                                        @{usersProfile.userName}
                                    </p>


                                    <p className="mt-1 text-sm text-gray-500">
                                        Employee #{usersProfile.employeeNumber}
                                    </p>

                                </div>


                                {/* Status */}

                                <div>
                            <span
                                className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                                    usersProfile.userName == true
                                        ? "bg-green-100 text-green-700"
                                        : "bg-gray-100 text-gray-600"
                                }`}
                            >

                                {usersProfile.isActive
                                    ? "Active"
                                    : "Inactive"}

                            </span>

                                </div>

                            </div>

                        </div>



                        <ProfileSection title="Personal Information">

                            <ProfileField
                                label="First Name"
                                value={usersProfile.firstName}
                            />

                            <ProfileField
                                label="Last Name"
                                value={usersProfile.lastName}
                            />

                            <ProfileField
                                label="Email"
                                value={usersProfile.email}
                                icon={<Mail size={17} />}
                            />

                            <ProfileField
                                label="Phone Number"
                                value={usersProfile.phoneNumber}
                                icon={<Phone size={17} />}
                            />

                        </ProfileSection>


                        {/* ==================================================
                    Address
                ================================================== */}

                        <ProfileSection title="Address">

                            <div className="sm:col-span-2">

                                <ProfileField
                                    label="Address"
                                    value={usersProfile.address}
                                    icon={<MapPin size={17} />}
                                />

                            </div>


                            <ProfileField
                                label="City"
                                value={usersProfile.city}
                            />


                            <ProfileField
                                label="State"
                                value={usersProfile.state}
                            />


                            <ProfileField
                                label="ZIP Code"
                                value={usersProfile.zip}
                            />

                        </ProfileSection>


                        {/* ==================================================
                    Account Information
                ================================================== */}

                        <ProfileSection title="Account Information">

                            <ProfileField
                                label="Username"
                                value={usersProfile.userName}
                                icon={<User size={17} />}
                            />


                            <ProfileField
                                label="Employee Number"
                                value={usersProfile.employeeNumber}
                            />

                        </ProfileSection>

                    </div>
                )}

            </div>

        </div>
    );
}


// ============================================================
// Profile Section
// ============================================================

interface ProfileSectionProps {
    title: string;
    children: React.ReactNode;
}


const ProfileSection: React.FC<ProfileSectionProps> = ({
                                                           title,
                                                           children,
                                                       }) => {

    return (
        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm">

            <h2 className="mb-5 text-lg font-semibold text-gray-900">

                {title}

            </h2>


            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">

                {children}

            </div>

        </div>
    );
};


// ============================================================
// Profile Field
// ============================================================

interface ProfileFieldProps {
    label: string;
    value?: string;
    icon?: React.ReactNode;
}


const ProfileField: React.FC<ProfileFieldProps> = ({
                                                       label,
                                                       value,
                                                       icon,
                                                   }) => {

    return (
        <div>

            <div className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-500">
                {icon}
                <span>
                    {label}
                </span>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900">
                {value || "-"}
            </div>

        </div>
    );
};